import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowUpRight, MapPin, Phone, Mail, Clock, Loader2 } from 'lucide-react';
import { usePage } from '../contexts/PageContext';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';
import RichText from '../components/RichText';
import api from '../lib/api';

const SUBJECTS = {
  en: ['New bank onboarding', 'Add a product', 'Pricing & proposal', 'Security review', 'Partnership', 'Other'],
  es: ['Onboarding de un banco', 'Agregar un producto', 'Precios y propuesta', 'Revisión de seguridad', 'Alianzas', 'Otro'],
};

export default function ContactPage() {
  const { t, seo } = usePage('contact');
  const { lang } = useLanguage();
  const nav = useNavigate();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', country: '', subject: '', message: '', consent: false });

  useEffect(() => {
    api.get('/public/countries').then((r) => setCountries(r.data || [])).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.consent) { setError(lang === 'es' ? 'Debes aceptar la política de privacidad.' : 'Please accept the privacy policy.'); return; }
    setLoading(true);
    try {
      await api.post('/public/contact', { ...form, lang });
      nav('/thank-you');
    } catch (err) {
      setError(err?.response?.data?.detail || (lang === 'es' ? 'No pudimos enviar tu mensaje.' : 'Could not send your message.'));
    } finally { setLoading(false); }
  };

  const subjects = SUBJECTS[lang] || SUBJECTS.en;

  return (
    <>
      <SEO title={seo.title} description={seo.description} keywords={seo.keywords} image={seo.og_image} />
      <section className="pt-24 pb-16 lg:pt-36 lg:pb-20">
        <div className="container-x">
          <span className="eyebrow">{t('hero_eyebrow', "Let's connect")}</span>
          <h1 className="heading-display mt-8 text-[44px] sm:text-[64px] lg:text-[96px] max-w-5xl">{t('hero_title', 'Tell us about your bank — we will tell you what is possible')}</h1>
          <p className="mt-8 text-[16px] text-[#C6C4CF] max-w-2xl"><RichText text={t('hero_description', '')} /></p>
        </div>
      </section>
      <section className="py-16 border-t border-[#3A332A]">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <h3 className="heading-display text-3xl lg:text-4xl">{t('info_title', 'Talk to ClicksyHub')}</h3>
            <ul className="mt-10 space-y-7">
              <Info Icon={MapPin} label={t('info_address_label', 'Address')} value={t('info_address', '')} />
              <Info Icon={Phone} label={t('info_phone_label', 'Phone')} value={t('info_phone', '')} href={`tel:${(t('info_phone', '') || '').replace(/[^+0-9]/g, '')}`} />
              <Info Icon={Mail} label={t('info_email_label', 'Email')} value={t('info_email', 'info@clicksyhub.com')} href={`mailto:${t('info_email', 'info@clicksyhub.com')}`} />
              <Info Icon={Clock} label={t('info_hours_label', 'Hours')} value={t('info_hours', '')} />
            </ul>
          </div>
          <form onSubmit={submit} className="lg:col-span-7 border-t border-[#3A332A] lg:border-t-0 lg:border-l lg:border-[#3A332A] lg:pl-12 pt-10 lg:pt-0">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={t('form_name', 'Full name')} required value={form.name} onChange={set('name')} />
              <Field label={t('form_email', 'Work email')} type="email" required value={form.email} onChange={set('email')} />
              <Field label={t('form_company', 'Company / Bank')} value={form.company} onChange={set('company')} />
              <Field label={t('form_phone', 'Phone')} value={form.phone} onChange={set('phone')} />
              <Select label={t('form_country', 'Country')} value={form.country} onChange={set('country')} options={countries.map(c => ({ value: c.code, label: c.name }))} />
              <Select label={t('form_subject', 'I am interested in')} value={form.subject} onChange={set('subject')} options={subjects.map(s => ({ value: s, label: s }))} />
            </div>
            <div className="mt-5">
              <label className="text-xs uppercase tracking-[0.22em] text-[#C6C4CF] font-medium">{t('form_message', 'Tell us more')}</label>
              <textarea required value={form.message} onChange={set('message')} rows={5} className="ch-input mt-2" />
            </div>
            <label className="mt-5 flex items-start gap-3 text-sm text-[#C6C4CF]">
              <input type="checkbox" checked={form.consent} onChange={set('consent')} className="mt-1 w-4 h-4 accent-[#C6C4CF]" />
              <span>{t('form_consent', 'I agree to the privacy policy and authorize ClicksyHub to contact me.')} <Link to="/privacy" className="underline text-[#FAF6EF] hover:text-[#F5F137]">Privacy</Link></span>
            </label>
            {error && <p className="mt-3 text-sm text-red-400">{error}</p>}
            <button type="submit" disabled={loading} className="btn-outline mt-8">
              {loading ? <Loader2 className="animate-spin" size={16} /> : <span>{t('form_submit', 'Send message')}</span>}
              <span className="arrow"><ArrowUpRight size={14} /></span>
            </button>
          </form>
        </div>
      </section>
    </>
  );
}

function Info({ Icon, label, value, href }) {
  const Inner = href ? 'a' : 'span';
  return (
    <li className="flex gap-5">
      <span className="w-12 h-12 rounded-full border border-[#C6C4CF] text-[#C6C4CF] flex items-center justify-center flex-shrink-0"><Icon size={18} /></span>
      <div>
        <p className="text-xs uppercase tracking-[0.22em] text-[#C6C4CF]">{label}</p>
        <Inner href={href} className="mt-1 block text-[#FAF6EF] text-[16px]">{value}</Inner>
      </div>
    </li>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.22em] text-[#C6C4CF] font-medium">{label}{props.required && ' *'}</span>
      <input {...props} className="ch-input mt-2" />
    </label>
  );
}

function Select({ label, options = [], ...props }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.22em] text-[#C6C4CF] font-medium">{label}</span>
      <select {...props} className="ch-input mt-2">
        <option value="">—</option>
        {options.map((o) => <option key={o.value} value={o.value} style={{ background: '#232017' }}>{o.label}</option>)}
      </select>
    </label>
  );
}
